import moment from 'moment';
import React, { memo, ReactNode, useEffect, useRef, useState } from 'react';
import Box from 'ui-box';
import { majorScale, minorScale } from '.';
import { Heading } from './Heading';
import { InputGroup } from './InputGroup';
import { Menu } from './Menu';
import { Popover } from './Popover';
import { Button } from './Button';

import { VariableSizeList } from 'react-window';
import { Placement } from '@popperjs/core';
import { FormattedMessage } from 'react-intl';

export interface IDateInputProps {
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  value?: Date;
  onChange?: (date: Date | null) => void;
  children?: ReactNode;
  placement?: Placement;
}

export const DateInput: React.FC<IDateInputProps> = ({
  value,
  onChange,
  placeholder = 'Vælg dato',
  minDate = moment().add(-3, 'years').toDate(),
  maxDate = moment().add(5, 'years').toDate(),
  children,
  placement,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [innerValue, setInnerValue] = useState('');
  const [prevInnerValue, setPrevInnerValue] = useState('');

  useEffect(() => {
    setInnerValue(value ? moment(value).format('L') : '');
    setPrevInnerValue(value ? moment(value).format('L') : '');
  }, [value]);

  const listRef = useRef<VariableSizeList>(null);

  const [months] = React.useMemo(() => {
    if (minDate.getTime() >= maxDate.getTime()) {
      throw new Error('minDate must be less than maxDate');
    }

    const startDate = moment(minDate);
    const endDate = moment(maxDate);
    const length =
      (endDate.year() - startDate.year()) * 12 +
      (endDate.month() - startDate.month());

    const months = new Array(length).fill(0).map((_, i) => {
      const month = (startDate.month() + i) % 12;
      const year = startDate.year() + Math.floor((startDate.month() + i) / 12);
      const date = moment().year(year).month(month);
      return {
        year,
        month,
        rows: Math.ceil(
          (date.daysInMonth() + date.startOf('month').weekday()) / 7
        ),
      };
    });

    return [months];
  }, [minDate, maxDate]);

  useEffect(() => {
    if (!isOpen) return;

    const target = value || new Date();
    const valueYear = moment(target).year();
    const valueMonth = moment(target).month();
    const index = months.findIndex((m) => {
      return m.year === valueYear && m.month === valueMonth;
    });
    if (index > -1 && listRef.current) {
      listRef.current.scrollToItem(index, 'start');
    }
  }, [isOpen, months, value]);

  const valueMoment = value ? moment(value).startOf('day') : undefined;

  const setDate = (date: Date | null) => {
    if (date && date.getTime() < minDate.getTime()) {
      return onChange && onChange(minDate);
    }
    if (date && date.getTime() > maxDate.getTime()) {
      return onChange && onChange(maxDate);
    }

    onChange && onChange(date);
  };

  const validateInnerValue = () => {
    if (innerValue === '') {
      setDate(null);
      return;
    }
    const date = moment(innerValue, 'L');
    if (date.isValid()) {
      setDate(date.toDate());
    } else {
      setInnerValue(moment(value).format('L'));
    }
  };

  return (
    <Popover
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      placement={placement}
      content={(close) => (
        <Menu
          overflow="hidden"
          position="relative"
          padding="0"
          onClick={(e) => e.stopPropagation()}
        >
          <VariableSizeList
            ref={listRef}
            height={332}
            itemSize={(index) => months[index].rows * 32 + 60}
            itemData={months}
            width={256}
            itemCount={months.length}
            overscanCount={1}
            className="hide-scrollbar"
            style={{ paddingBottom: '64px' }}
          >
            {({ index, style }) => (
              <div style={style}>
                <MonthMenu
                  month={months[index].month}
                  year={months[index].year}
                  activeMoment={valueMoment}
                  onSelect={(date) => {
                    close();
                    setDate(date);
                  }}
                />
              </div>
            )}
          </VariableSizeList>
          <Box
            background="linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 35%, rgba(255,255,255,1) 100%)"
            position="absolute"
            width="100%"
            bottom={0}
            zIndex={0}
            paddingTop="16px"
            borderRadius="6px"
          >
            <Box
              display="flex"
              justifyContent="space-between"
              padding={majorScale(1)}
            >
              <Button
                minimal
                onClick={() => {
                  setDate(null);
                  close();
                }}
              >
                <FormattedMessage defaultMessage="No date" />
              </Button>
              <Button
                minimal
                intent="info"
                onClick={() => {
                  setDate(new Date());
                  close();
                }}
              >
                <FormattedMessage defaultMessage="Today" />
              </Button>
            </Box>
          </Box>
        </Menu>
      )}
    >
      {children ? (
        children
      ) : (
        <InputGroup
          placeholder={placeholder}
          value={innerValue}
          onChange={(e) => {
            setInnerValue(e.target.value);
            setPrevInnerValue(innerValue);
          }}
          onBlur={() => innerValue !== prevInnerValue && validateInnerValue()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              validateInnerValue();
            }
          }}
        />
      )}
    </Popover>
  );
};

const MonthMenu = memo(
  ({
    month,
    year,
    activeMoment,
    onSelect,
  }: {
    month: number;
    year: number;
    activeMoment?: moment.Moment;
    onSelect: (date: Date) => void;
  }) => {
    const [date, days, daysOffset, activeMonth] = React.useMemo(() => {
      const date = moment().year(year).month(month).startOf('month');
      // We need to be carefull here - as we use Monday as the first day of the week.
      // If sunday is the first day of the month, we should render 6 empty places
      const daysOffset = date.weekday();
      const length = date.daysInMonth();
      const days = new Array(length).fill(0).map((_, i) =>
        moment()
          .year(year)
          .month(month)
          .date(i + 1)
          .startOf('day')
      );
      const activeMonth =
        activeMoment &&
        activeMoment.year() === year &&
        activeMoment.month() === month;

      return [date, days, daysOffset, activeMonth];
    }, [year, month, activeMoment]);

    return (
      <Box
        textAlign="center"
        width="256px"
        paddingX={majorScale(2)}
        paddingTop={majorScale(2)}
        marginX="auto"
      >
        <Heading size={300} textTransform="capitalize">
          {date.format('MMMM YYYY')}
        </Heading>
        <Box
          display="grid"
          gridTemplateColumns="repeat(7, 32px)"
          marginTop={majorScale(1)}
          marginBottom={minorScale(1)}
        >
          {new Array(7).fill(0).map((_, i) => (
            <p
              key={i}
              className="small muted"
              style={{ textTransform: 'capitalize' }}
            >
              {moment().weekday(i).format('ddd')}
            </p>
          ))}
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(7, 32px)">
          {days.map((d, i) => (
            <Button
              key={i}
              minimal
              intent={
                activeMonth && activeMoment && activeMoment.isSame(d, 'date')
                  ? 'info'
                  : 'none'
              }
              gridColumnStart={i === 0 ? daysOffset + 1 : undefined}
              className={
                activeMonth && activeMoment && activeMoment.isSame(d, 'date')
                  ? 'hover'
                  : d.isSame(moment(), 'date')
                  ? 'hover'
                  : undefined
              }
              onClick={() => onSelect(d.toDate())}
            >
              {d.date()}
            </Button>
          ))}
        </Box>
      </Box>
    );
  }
);
